import { render } from "preact";
import { ChatWidget } from "./components/ChatWidget";
import { getChatStyles } from "./styles/chatStyles";
import type {
  WidgetInitConfig,
  WidgetInstance,
  WidgetMode,
} from "./types/widget";

/** Default dimensions for embedded mode */
const DEFAULT_EMBEDDED_WIDTH = 1200;
const DEFAULT_EMBEDDED_HEIGHT = 800;

declare global {
  interface Window {
    PankAgent: {
      init: (config: WidgetInitConfig) => void;
      open: () => void;
      close: () => void;
      destroy: () => void;
    };
  }
}

let overlayContainer: HTMLDivElement | null = null;
let embeddedContainer: HTMLElement | null = null;
let widgetInstance: WidgetInstance | null = null;
let currentMode: WidgetMode = "overlay";

/**
 * Detect if there's an embedded container and get its dimensions
 */
function detectEmbeddedContainer(): {
  container: HTMLElement | null;
  width: number;
  height: number;
} {
  const container = document.getElementById("pankagent");
  if (!container) {
    return { container: null, width: 0, height: 0 };
  }

  // Read dimensions from data attributes
  const width =
    parseInt(container.dataset.width || "", 10) || DEFAULT_EMBEDDED_WIDTH;
  const height =
    parseInt(container.dataset.height || "", 10) || DEFAULT_EMBEDDED_HEIGHT;

  return { container, width, height };
}

/**
 * Initialize widget in overlay mode (floating bubble)
 */
function initOverlay(config: WidgetInitConfig) {
  if (overlayContainer) {
    console.warn("PankAgent overlay already initialized");
    return;
  }

  // Create container
  overlayContainer = document.createElement("div");
  overlayContainer.id = "pankagent-widget";
  document.body.appendChild(overlayContainer);

  // Create shadow DOM for style isolation
  const shadow = overlayContainer.attachShadow({ mode: "open" });

  // Inject styles into shadow DOM
  const styleSheet = document.createElement("style");
  styleSheet.textContent = getChatStyles({ primaryColor: config.primaryColor });
  shadow.appendChild(styleSheet);

  // Create render target
  const root = document.createElement("div");
  root.id = "pankagent-root";
  shadow.appendChild(root);

  // Render widget
  render(
    <ChatWidget
      config={{ ...config, mode: "overlay" }}
      onMount={(instance) => {
        widgetInstance = instance;
      }}
    />,
    root
  );

  console.log("✅ PankAgent initialized (overlay mode)");
}

/**
 * Initialize widget in embedded mode (inside container)
 */
function initEmbedded(
  config: WidgetInitConfig,
  container: HTMLElement,
  width: number,
  height: number
) {
  if (embeddedContainer) {
    console.warn("PankAgent embedded already initialized");
    return;
  }

  embeddedContainer = container;

  // Create shadow DOM for style isolation
  const shadow = container.attachShadow({ mode: "open" });

  // Inject styles into shadow DOM
  const styleSheet = document.createElement("style");
  styleSheet.textContent = getChatStyles({
    primaryColor: config.primaryColor,
    embedded: true,
  });
  shadow.appendChild(styleSheet);

  // Create render target
  const root = document.createElement("div");
  root.id = "pankagent-embedded-root";
  root.style.width = "100%";
  root.style.height = "100%";
  shadow.appendChild(root);

  // Set container dimensions
  container.style.width = `${width}px`;
  container.style.height = `${height}px`;

  // Render embedded widget using ChatWidget with embedded mode
  render(
    <ChatWidget
      config={{
        ...config,
        mode: "embedded",
        dimensions: { width, height },
      }}
    />,
    root
  );

  console.log(`✅ PankAgent initialized (embedded mode, ${width}x${height})`);
}

const init = (config: WidgetInitConfig) => {
  // Check for embedded container
  const { container, width, height } = detectEmbeddedContainer();

  if (container) {
    currentMode = "embedded";
    initEmbedded(config, container, width, height);
  } else {
    currentMode = "overlay";
    initOverlay(config);
  }
};

const open = () => {
  if (currentMode === "overlay") {
    widgetInstance?.open();
  }
  // In embedded mode, widget is always open
};

const close = () => {
  if (currentMode === "overlay") {
    widgetInstance?.close();
  }
  // In embedded mode, widget cannot be closed
};

const destroy = () => {
  if (overlayContainer) {
    overlayContainer.remove();
    overlayContainer = null;
  }
  if (embeddedContainer) {
    // Clear shadow DOM content
    if (embeddedContainer.shadowRoot) {
      embeddedContainer.shadowRoot.innerHTML = "";
    }
    embeddedContainer = null;
  }
  widgetInstance = null;
  currentMode = "overlay";
};

// Expose to window
// Clear any legacy pankagent variable from old snippet
if (typeof (window as any).pankagent === "string") {
  delete (window as any).pankagent;
}

window.PankAgent = {
  init,
  open,
  close,
  destroy,
};

export { init, open, close, destroy };
